import { useState, useEffect } from 'react';
import { getKidsProgramBlocks } from '../../utils/api';

// Contributing Teams — read-only view of every main-team block tagged with a
// KidsProgram, grouped by day then service time (morning / afternoon / evening)
// so all the teams serving the same slot sit together, even though every team
// names its blocks differently. Rostered by the main-team leads; Mal only views.

const DAY_ORDER = ['Thursday', 'Friday', 'Saturday', 'Sunday', 'Monday'];
const BUCKETS = ['Morning', 'Afternoon', 'Evening'];

function bucketOf(start) {
  const h = parseInt((start || '').split(':')[0], 10);
  if (isNaN(h)) return 'Morning';
  return h < 12 ? 'Morning' : h < 17 ? 'Afternoon' : 'Evening';
}

function fmtTime(t) {
  if (!t) return '';
  const [h, m] = (t || '').split(':').map(Number);
  if (isNaN(h)) return t;
  return `${h % 12 || 12}:${String(m || 0).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

export default function KidsContributing({ showToast }) {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getKidsProgramBlocks()
      .then(d => setBlocks(d || []))
      .catch(() => showToast('Could not load contributing teams.'))
      .finally(() => setLoading(false));
  }, [showToast]);

  const days = DAY_ORDER.filter(d => blocks.some(b => b.day === d));
  // Any day not in the canonical order still renders at the end.
  blocks.forEach(b => { if (b.day && !days.includes(b.day)) days.push(b.day); });

  return (
    <div className="page-content">
      <div className="page-title-row">
        <div>
          <div className="page-title">Contributing Teams</div>
          <div className="page-sub">Read-only · main teams rostering into Kids programs, grouped by day &amp; service</div>
        </div>
      </div>

      {loading ? (
        <div className="panel" style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300 }}>Loading…</div>
      ) : blocks.length === 0 ? (
        <div className="panel" style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300 }}>
          No main-team positions are marked for Kids yet. Main team leads check the "Kids" box on a
          position when scheduling (on teams with Kids scheduling enabled), and it shows up here.
        </div>
      ) : days.map(day => (
        <div key={day} style={{ marginBottom: 28 }}>
          <div className="page-title" style={{ fontSize: 20, marginBottom: 12 }}>{day}</div>
          {BUCKETS.map(bk => {
            const inBucket = blocks
              .filter(b => b.day === day && bucketOf(b.startTime) === bk)
              .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
            if (inBucket.length === 0) return null;
            return (
              <div key={bk} style={{ marginBottom: 16 }}>
                <div className="section-header">{bk}</div>
                <div className="panel">
                  {inBucket.map((b, i) => (
                    <div key={b.blockId} style={{ borderBottom: i < inBucket.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <div className="block-card-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span className="block-card-title">{b.teamName}</span>
                          {b.kidsProgram && <span className="pill" style={{ background: '#6AA292', color: '#fff', fontSize: 9 }}>{b.kidsProgram}</span>}
                          <span className="block-card-meta">{b.title} · {fmtTime(b.startTime)}{b.endTime ? ` – ${fmtTime(b.endTime)}` : ''}</span>
                          <span style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, color: b.filledCount >= b.positionCount ? '#1a5c3a' : '#b5700a' }}>
                            {b.filledCount}/{b.positionCount} filled
                          </span>
                        </div>
                      </div>
                      {(b.positions || []).map((p, j) => (
                        <div key={j} className="block-position-row">
                          <div className="block-pos-label">{p.positionLabel || 'Position'}</div>
                          <div className="block-pos-person">
                            {p.personName
                              ? <><span className={`status-dot ${p.status === 'accepted' ? 'dot-green' : 'dot-amber'}`} style={{ marginRight: 8 }} />{p.personName}</>
                              : <span style={{ color: 'var(--subtle)', fontStyle: 'italic' }}>Open position</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
